import {
  buildPageEvent,
  GeoLocation,
  NinetailedRequestContext,
  NinetailedApiClient,
  SelectedVariantInfo,
  Traits,
} from '@ninetailed/experience.js-shared';
import { NinetailedAPIClient as NinetailedApiClientNode } from '@ninetailed/experience.js-node';
import { v4 as uuid } from 'uuid';
import { RequestCookies } from 'next/dist/compiled/@edge-runtime/cookies';
import serverContext from 'server-only-context';
import { NINETAILED_ANONYMOUS_ID_COOKIE } from 'utils/cookies';
import { isDraftPreviewEnabled } from 'utils/draftPreview';
import { NinetailedMode } from 'types/constants';
import { isE2ETesting } from 'utils/environment';

export type SelectedVariant = { experienceId: string; variantIndex: number };

type SendPagePayload = {
  ctx: NinetailedRequestContext;
  cookies: RequestCookies;
  clientId: string;
  environment?: string;
  url?: string;
  ip?: string;
  location?: GeoLocation;
  queryParams?: string;
};

type SendEventOptions = {
  anonymousId?: string;
  timestamp?: number;
};

export const createRequestContext = (request: Request): NinetailedRequestContext => {
  return {
    url: request.url,
    locale: 'en-US', // One would make this dynamic if handling multiple locales
    referrer: request.headers.get('referer') || '',
    userAgent: request.headers.get('user-agent') || '',
  };
};

export const sendPageEvent = async ({
  clientId,
  environment,
  ctx,
  cookies,
  url,
  ip,
  location,
  queryParams,
}: SendPagePayload) => {
  const apiClient = new NinetailedApiClient({ clientId, environment, url });

  let ninetailedId = cookies.get(NINETAILED_ANONYMOUS_ID_COOKIE)?.value;
  if (!ninetailedId) {
    // If no ninetailedId, generate a new one using a 64-character hex string
    const chars = '0123456789abcdef';
    ninetailedId = Array.from({ length: 64 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  }

  // Traits are the way we configure audiences in Ninetailed
  // for the CURRENT page location. If we update the trait on each
  // page view, we can use it to target users based on the current
  // page, query params, etc.
  const traits: Traits = {};

  // If the page has query parameters, set the query_params trait
  // and send an identify call to Ninetailed
  if (ninetailedId) {
    traits.query_params = queryParams || '';
    try {
      await sendIdentifyCall(ninetailedId, traits);
    } catch (error) {
      console.error('Failed to send identify call:', error);
    }
  }

  const pageEvent = buildPageEvent({
    ctx,
    messageId: uuid(),
    timestamp: Date.now(),
    properties: {},
  });

  try {
    return await apiClient.upsertProfile(
      {
        profileId: ninetailedId,
        events: [{ ...pageEvent, context: { ...pageEvent.context, location } }],
      },
      { ip },
    );
  } catch (error) {
    console.error('Failed to upsert profile:', error);
    // Return a fallback response to maintain function contract
    return { profile: { id: ninetailedId }, experiences: [] };
  }
};

export const sendIdentifyCall = async (ninetailedId: string, traits: Traits) => {
  const apiClient = new NinetailedApiClientNode({
    clientId: process.env.NEXT_PUBLIC_NINETAILED_API_KEY!,
    environment: 'main',
    url: process.env.NEXT_PUBLIC_NINETAILED_URL!,
  });

  await upsertProfileViaIdentify(apiClient, {
    id: ninetailedId,
    traits,
  });
};

async function upsertProfileViaIdentify(
  apiClient: NinetailedApiClientNode,
  { id, traits, options }: { id: string; traits: Traits; options?: SendEventOptions },
) {
  const response = await apiClient.sendIdentifyEvent(id, traits, options);
  return response;
}

export const encodeExperienceSelections = (selections: SelectedVariantInfo[]): string => {
  return selections
    .map(selection => {
      return {
        experienceId: selection.experienceId,
        variantIndex: selection.variantIndex,
      };
    })
    .map(selection => {
      return `${selection.experienceId}${encodeURIComponent('=')}${selection.variantIndex}`;
    })
    .sort()
    .join(encodeURIComponent(','));
};

export const decodeExperienceSelections = (encodedExperienceVariantsMap: string): Record<string, number> => {
  return encodedExperienceVariantsMap
    .split(encodeURIComponent(','))
    .map(experienceIdWithVariant => {
      const [experienceId, _variantIndex] = experienceIdWithVariant.split(encodeURIComponent('='));

      const variantIndex = parseInt(_variantIndex);

      if (!experienceId || variantIndex === undefined || isNaN(variantIndex)) {
        return null;
      }

      return { experienceId, variantIndex };
    })
    .filter((x): x is SelectedVariant => !!x)
    .reduce((acc, curr) => ({ ...acc, [curr.experienceId]: curr.variantIndex }), {});
};

export const [getExperiences, setExperiences] = serverContext<Record<string, number> | null>(null);
export const [getPageHasExperiments, setPageHasExperiments] = serverContext<boolean>(false);

export const getNinetailedMode: () => Promise<NinetailedMode> = async () => {
  // We use this to override the mode when needed
  // It's handy for local development in .env.local
  // or if we need to override and entire environment like QA
  const overrideMode = process.env.NEXT_PUBLIC_NINETAILED_MODE;

  // If an override mode is set and valid, use it
  if (overrideMode === 'server' || overrideMode === 'client') {
    return overrideMode;
  }

  // Otherwise determine the rendering mode based on whether the env enables draft preview
  return (await isDraftPreviewEnabled()) ? 'client' : 'server';
};

export async function getSlugAndExperiences({ params }: { params: Promise<{ slug: string[] }> }) {
  const rawSlug = (await params)?.slug || [];
  const slug = rawSlug.join('/');

  // importing headers here in-line allows
  // this function to be used both client + server-side
  const { headers } = await import('next/headers');
  const headersList = await headers();

  // Get experience selections from header
  const experienceSelections = headersList.get('x-ninetailed-experiences');
  const selectedExperiences = experienceSelections ? decodeExperienceSelections(experienceSelections) : null;

  return {
    selectedExperiences,
    slug,
  };
}

/**
 * Determines whether to force baseline rendering for Ninetailed experiences.
 * This function centralizes all conditions that should force baseline rendering.
 */
export function shouldRenderBaseline(): boolean {
  // Always render baseline in CI environments to prevent flaky tests or unintended VRT failures
  if (isE2ETesting()) {
    return true;
  }

  // Future conditions can be added here:
  // - Feature flags (e.g., disable personalization for certain users)
  // - User preferences (e.g., opt-out of personalization)
  // - Performance considerations (e.g., disable on slow connections)

  return false;
}
